import { useState, useEffect, useCallback } from 'react';
import { updateConsent } from '@/services/analytics';
import { secureGetItem, secureSetItem } from '@/utils/secure-storage';

const STORAGE_KEY = 'cookie_consent';

type ConsentChoice = 'accepted' | 'declined';

async function getStoredChoice(): Promise<ConsentChoice | null> {
  try {
    const value = await secureGetItem(localStorage, STORAGE_KEY);
    if (value === 'accepted' || value === 'declined') return value;
  } catch {
    // Storage unavailable
  }
  return null;
}

async function persistChoice(choice: ConsentChoice): Promise<void> {
  try {
    await secureSetItem(localStorage, STORAGE_KEY, choice);
  } catch {
    // Storage unavailable
  }
}

/**
 * GDPR cookie consent banner.
 *
 * - Defaults to hidden if user already made a choice
 * - Calls `updateConsent()` from analytics service on accept/decline
 * - Uses design-system CSS custom properties for dark/light theming
 * - Fixed to bottom of viewport, dismissible
 */
export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const check = async () => {
      const stored = await getStoredChoice();
      if (!stored) {
        setVisible(true);
      }
    };
    check();
  }, []);

  const handleAccept = useCallback(() => {
    persistChoice('accepted');
    updateConsent(true);
    setVisible(false);
  }, []);

  const handleDecline = useCallback(() => {
    persistChoice('declined');
    updateConsent(false);
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        padding: 'var(--space-4)',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          maxWidth: '480px',
          marginLeft: 'auto',
          marginRight: 'auto',
          backgroundColor: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-lg)',
          padding: 'var(--space-5)',
          pointerEvents: 'auto',
        }}
      >
        <p
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
            lineHeight: 'var(--leading-relaxed)',
            margin: 0,
          }}
        >
          We use cookies to understand how you use our site and improve your
          experience. You can accept or decline non-essential cookies.
        </p>

        <div
          style={{
            display: 'flex',
            gap: 'var(--space-3)',
            marginTop: 'var(--space-4)',
          }}
        >
          <button
            type="button"
            onClick={handleAccept}
            style={{
              flex: 1,
              padding: 'var(--space-2) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              backgroundColor: 'var(--color-primary-600)',
              color: 'var(--color-white)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              transition: 'background-color var(--duration-fast) var(--ease-out)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-primary-500)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-primary-600)';
            }}
          >
            Accept
          </button>
          <button
            type="button"
            onClick={handleDecline}
            style={{
              flex: 1,
              padding: 'var(--space-2) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-default)',
              backgroundColor: 'var(--bg-surface)',
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              transition:
                'background-color var(--duration-fast) var(--ease-out), color var(--duration-fast) var(--ease-out)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-button-secondary-hover)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-surface)';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
};
